/**
 * Camunda 7.18 Variable Instance REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/variable-instance/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type { CountResult } from '../types';

export interface VariableInstance {
  id: string;
  name: string;
  type: string;
  value: any;
  valueInfo: Record<string, any>;
  processDefinitionId: string | null;
  processInstanceId: string | null;
  executionId: string | null;
  caseInstanceId: string | null;
  caseExecutionId: string | null;
  taskId: string | null;
  batchId: string | null;
  activityInstanceId: string | null;
  errorMessage: string | null;
  tenantId: string | null;
}

export interface VariableInstanceQuery {
  variableName?: string;
  variableNameLike?: string;
  processInstanceIdIn?: string[];
  executionIdIn?: string[];
  caseInstanceIdIn?: string[];
  caseExecutionIdIn?: string[];
  taskIdIn?: string[];
  batchIdIn?: string[];
  activityInstanceIdIn?: string[];
  tenantIdIn?: string[];
  variableValues?: {
    name: string;
    operator: 'eq' | 'neq' | 'gt' | 'gteq' | 'lt' | 'lteq' | 'like';
    value: any;
  }[];
  variableNamesIgnoreCase?: boolean;
  variableValuesIgnoreCase?: boolean;
  variableScopeIdIn?: string[];
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  firstResult?: number;
  maxResults?: number;
}

export class VariableService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/variable-instance';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get List
  // ============================================================================

  /**
   * Get a list of variable instances
   */
  async getList(query?: VariableInstanceQuery): Promise<VariableInstance[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<VariableInstance[]>(`${this.basePath}${params}`);
  }

  /**
   * Get variable instances count
   */
  async getCount(query?: VariableInstanceQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  /**
   * Query variable instances (POST)
   */
  async queryList(query: VariableInstanceQuery): Promise<VariableInstance[]> {
    const { firstResult, maxResults, sortBy, sortOrder, ...body } = query;
    const params = this.buildPaginationParams({ firstResult, maxResults, sortBy, sortOrder });
    return this.client.post<VariableInstance[]>(`${this.basePath}${params}`, body);
  }

  /**
   * Query variable instances count (POST)
   */
  async queryCount(query: VariableInstanceQuery): Promise<CountResult> {
    return this.client.post<CountResult>(`${this.basePath}/count`, query);
  }

  // ============================================================================
  // Get Single
  // ============================================================================

  /**
   * Get variable instance by ID
   */
  async getById(
    id: string,
    options?: { deserializeValue?: boolean }
  ): Promise<VariableInstance> {
    const params = options?.deserializeValue !== undefined
      ? `?deserializeValue=${options.deserializeValue}`
      : '';
    return this.client.get<VariableInstance>(`${this.basePath}/${id}${params}`);
  }

  /**
   * Get variable instance binary data
   */
  async getBinary(id: string): Promise<Blob> {
    return this.client.getBlob(`${this.basePath}/${id}/data`);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: VariableInstanceQuery): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          if (key === 'variableValues') {
            params.append(key, JSON.stringify(value));
          } else {
            params.append(key, value.join(','));
          }
        } else if (typeof value === 'object') {
          params.append(key, JSON.stringify(value));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  private buildPaginationParams(params: {
    firstResult?: number;
    maxResults?: number;
    sortBy?: string;
    sortOrder?: string;
  }): string {
    const urlParams = new URLSearchParams();
    if (params.firstResult !== undefined) urlParams.append('firstResult', String(params.firstResult));
    if (params.maxResults !== undefined) urlParams.append('maxResults', String(params.maxResults));
    if (params.sortBy) urlParams.append('sortBy', params.sortBy);
    if (params.sortOrder) urlParams.append('sortOrder', params.sortOrder);
    const queryString = urlParams.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let variableServiceInstance: VariableService | null = null;

export function getVariableService(): VariableService {
  if (!variableServiceInstance) {
    variableServiceInstance = new VariableService();
  }
  return variableServiceInstance;
}

export default VariableService;
